import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Calendar, Clock, MapPin, Plus, ChevronLeft, ChevronRight,
  Dumbbell, Trophy, School, Church, Car, Moon, Users, Zap,
  CheckCircle, XCircle, RefreshCw, Target, Flame, Brain,
  AlertTriangle, Play, Pause, Info, AlertCircle, HelpCircle,
  ArrowRight, Sparkles, PartyPopper, Rocket, Edit2, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, isToday } from 'date-fns';
import TournamentWizard from '../components/TournamentWizard';

const BLOCK_TYPES = [
  { value: 'school', label: 'School', icon: School, color: 'bg-blue-500' },
  { value: 'work', label: 'Work', icon: Dumbbell, color: 'bg-gray-500' },
  { value: 'church', label: 'Church', icon: Church, color: 'bg-purple-500' },
  { value: 'travel', label: 'Travel', icon: Car, color: 'bg-orange-500' },
  { value: 'sleep', label: 'Sleep', icon: Moon, color: 'bg-indigo-500' },
  { value: 'family', label: 'Family', icon: Users, color: 'bg-pink-500' },
  { value: 'other', label: 'Other', icon: Clock, color: 'bg-gray-400' }
];

const TRAINING_TYPES = [
  { value: 'practice', label: 'Team Practice', points: 20, color: 'bg-green-500' },
  { value: 'strength', label: 'Strength Training', points: 25, color: 'bg-red-500' },
  { value: 'conditioning', label: 'Conditioning', points: 20, color: 'bg-orange-500' },
  { value: 'speed', label: 'Speed/Agility', points: 20, color: 'bg-yellow-500' },
  { value: 'skills', label: 'Skills Work', points: 15, color: 'bg-blue-500' },
  { value: 'scrimmage', label: 'Scrimmage', points: 25, color: 'bg-purple-500' },
  { value: 'film', label: 'Film Study', points: 10, color: 'bg-gray-500' },
  { value: 'personal_training', label: 'Personal Training', points: 30, color: 'bg-pink-500' }
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Smart title detection keywords for guidance
const BLOCK_KEYWORDS = ['school', 'class', 'church', 'work', 'job', 'sleep', 'family', 'dinner', 'breakfast', 'lunch'];
const TRAINING_KEYWORDS = ['practice', 'training', 'workout', 'gym', 'lift', 'weight', 'conditioning', 'speed', 'agility', 'coach', 'trainer', 'S&C', 's&c', 'strength'];
const GAME_KEYWORDS = ['game', 'match', 'tournament', 'competition', 'championship', 'showcase', 'tryout', 'scrimmage vs', 'play against'];

// Smart suggestion helper
const detectMisplacedItem = (title, currentType) => {
  const lowerTitle = title.toLowerCase();
  
  if (currentType === 'block') {
    // Check if it should be training
    if (TRAINING_KEYWORDS.some(k => lowerTitle.includes(k))) {
      return {
        suggested: 'training',
        message: `"${title}" sounds like a training session. Use "Add Training" instead for practices, workouts, or coaching sessions — they count toward your progress!`,
        icon: 'training'
      };
    }
    // Check if it should be tournament/game
    if (GAME_KEYWORDS.some(k => lowerTitle.includes(k))) {
      return {
        suggested: 'tournament',
        message: `"${title}" sounds like a game or competition. Use "Add Tournament" instead — it auto-creates prep and recovery windows!`,
        icon: 'game'
      };
    }
  }
  
  if (currentType === 'training') {
    // Check if it should be a block
    if (BLOCK_KEYWORDS.some(k => lowerTitle.includes(k)) && !TRAINING_KEYWORDS.some(k => lowerTitle.includes(k))) {
      return {
        suggested: 'block',
        message: `"${title}" sounds like unavailable time. Use "Add Block" for times when you CAN'T train (school, church, work).`,
        icon: 'block'
      };
    }
    // Check if it should be tournament/game
    if (GAME_KEYWORDS.some(k => lowerTitle.includes(k))) {
      return {
        suggested: 'tournament',
        message: `"${title}" sounds like a game. Use "Add Tournament" for games — they don't count as training but get special prep/recovery windows!`,
        icon: 'game'
      };
    }
  }
  
  if (currentType === 'tournament') {
    // Check if it should be training
    if (TRAINING_KEYWORDS.some(k => lowerTitle.includes(k)) && !GAME_KEYWORDS.some(k => lowerTitle.includes(k))) {
      return {
        suggested: 'training',
        message: `"${title}" sounds like practice, not a game. Use "Add Training" for practices — they award Aura points!`,
        icon: 'training'
      };
    }
  }
  
  return null;
};

const CalendarHubPage = () => {
  const { user } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [calendarData, setCalendarData] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [adherenceStats, setAdherenceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [showSmartScheduleDialog, setShowSmartScheduleDialog] = useState(false);
  const [showTournamentDialog, setShowTournamentDialog] = useState(false);
  const [showTournamentWizard, setShowTournamentWizard] = useState(false);  // NEW: Tournament Wizard 2.0
  
  // Form states
  const [newBlock, setNewBlock] = useState({
    title: '', block_type: 'school', days_of_week: [],
    start_time: '08:00', end_time: '15:00', notes: ''
  });
  
  const [smartScheduleRequest, setSmartScheduleRequest] = useState({
    workouts_per_week: 4,
    preferred_duration: 45,
    preferred_times: ['morning', 'evening'],
    avoid_game_days: true,
    recovery_day_after_games: true
  });
  
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  
  // Tournament form state
  const [newTournament, setNewTournament] = useState({
    name: '',
    location: '',
    start_date: '',
    end_date: '',
    games: [{ opponent: '', start_time: '', end_time: '' }],
    notes: ''
  });
  
  // Training Preset states
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [trainingPresets, setTrainingPresets] = useState([]);
  const [weeklyTrainingSchedule, setWeeklyTrainingSchedule] = useState(null);
  const [newTrainingPreset, setNewTrainingPreset] = useState({
    name: '',
    training_type: 'practice',
    days_of_week: [],
    start_time: '16:00',
    end_time: '18:00',
    location: '',
    coach_name: '',
    notes: ''
  });
  
  // Smart guidance states
  const [blockSuggestion, setBlockSuggestion] = useState(null);
  const [trainingSuggestion, setTrainingSuggestion] = useState(null);
  const [tournamentSuggestion, setTournamentSuggestion] = useState(null);
  
  // Setup Wizard states
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  
  // Smart Prompt states
  const [showNextStepPrompt, setShowNextStepPrompt] = useState(false);
  const [nextStepType, setNextStepType] = useState(null); // 'after_block', 'after_training', 'after_tournament', 'after_schedule'
  
  // Edit/Delete states
  const [showEditBlockDialog, setShowEditBlockDialog] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showEditTrainingDialog, setShowEditTrainingDialog] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [showManageItemsDialog, setShowManageItemsDialog] = useState(false);
  const [manageItemsTab, setManageItemsTab] = useState('blocks'); // 'blocks', 'training', 'tournaments'

  useEffect(() => {
    fetchCalendarData();
    fetchBlocks();
    fetchAdherenceStats();
    fetchTrainingPresets();
    fetchWeeklyTrainingSchedule();
    fetchTournaments();
  }, [currentWeek]);
  
  // Check if user needs setup wizard (new user with no calendar config)
  useEffect(() => {
    const checkSetupNeeded = () => {
      const hasCompletedSetup = localStorage.getItem('aura_calendar_setup_completed');
      if (!hasCompletedSetup && !loading && blocks.length === 0 && trainingPresets.length === 0) {
        // New user - show wizard after a brief delay
        setTimeout(() => setShowSetupWizard(true), 1000);
      }
    };
    checkSetupNeeded();
  }, [loading, blocks, trainingPresets]);

  const fetchCalendarData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const startDate = format(currentWeek, 'yyyy-MM-dd');
      const endDate = format(endOfWeek(currentWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      
      const response = await axios.get(
        `${API}/calendar-hub/unified-view?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCalendarData(response.data.calendar || {});
      // Tournaments are fetched separately via fetchTournaments()
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/calendar-hub/availability-blocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocks(response.data.blocks || []);
    } catch (error) {
      console.error('Failed to fetch blocks:', error);
    }
  };

  const fetchAdherenceStats = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/calendar-hub/adherence-stats?period=month`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdherenceStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch adherence:', error);
    }
  };

  const fetchTrainingPresets = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/calendar-hub/training-presets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingPresets(response.data.presets || []);
    } catch (error) {
      console.error('Failed to fetch training presets:', error);
    }
  };

  const fetchWeeklyTrainingSchedule = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/calendar-hub/training-presets/weekly-schedule?week_start=${weekStart}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyTrainingSchedule(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly training:', error);
    }
  };
  
  const fetchTournaments = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/calendar-hub/tournaments?upcoming_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data.tournaments || []);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    }
  };

  const handleCreateTrainingPreset = async (e) => {
    e.preventDefault();
    if (newTrainingPreset.days_of_week.length === 0) {
      toast.error('Select at least one day');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/calendar-hub/training-presets`, newTrainingPreset, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Training preset created! It will appear on your calendar.');
      setShowTrainingDialog(false);
      setNewTrainingPreset({
        name: '', training_type: 'practice', days_of_week: [],
        start_time: '16:00', end_time: '18:00', location: '', coach_name: '', notes: ''
      });
      fetchTrainingPresets();
      fetchWeeklyTrainingSchedule();
      
      // If in wizard mode, advance to next step
      if (showSetupWizard && wizardStep === 2) {
        setWizardStep(3);
        setTimeout(() => setShowSetupWizard(true), 300);
      } else if (!showSetupWizard) {
        // Show smart prompt for next step
        setNextStepType('after_training');
        setShowNextStepPrompt(true);
      }
    } catch (error) {
      toast.error('Failed to create training preset');
    }
  };

  const handleCompleteTraining = async (presetId, date) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/calendar-hub/training-presets/complete`, {
        preset_id: presetId,
        date: date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.already_completed) {
        toast.info('Training already logged for this date');
      } else {
        toast.success(response.data.message);
        fetchWeeklyTrainingSchedule();
        fetchAdherenceStats();
      }
    } catch (error) {
      toast.error('Failed to complete training');
    }
  };

  const handleDeleteTrainingPreset = async (presetId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/calendar-hub/training-presets/${presetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Training preset deleted');
      fetchTrainingPresets();
      fetchWeeklyTrainingSchedule();
    } catch (error) {
      toast.error('Failed to delete preset');
    }
  };

  const toggleTrainingDay = (dayNum) => {
    setNewTrainingPreset(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayNum)
        ? prev.days_of_week.filter(d => d !== dayNum)
        : [...prev.days_of_week, dayNum]
    }));
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (newBlock.days_of_week.length === 0) {
      toast.error('Select at least one day');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/calendar-hub/availability-blocks`, newBlock, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Availability block created');
      setShowBlockDialog(false);
      setNewBlock({ title: '', block_type: 'school', days_of_week: [], start_time: '08:00', end_time: '15:00', notes: '' });
      fetchBlocks();
      fetchCalendarData();
      
      // If in wizard mode, advance to next step
      if (showSetupWizard && wizardStep === 1) {
        setWizardStep(2);
        setTimeout(() => setShowSetupWizard(true), 300);
      } else if (!showSetupWizard) {
        // Show smart prompt for next step
        setNextStepType('after_block');
        setShowNextStepPrompt(true);
      }
    } catch (error) {
      toast.error('Failed to create block');
    }
  };

  // Edit Block
  const handleEditBlock = async (e) => {
    e.preventDefault();
    if (!editingBlock) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/calendar-hub/availability-blocks/${editingBlock.block_id}`, {
        title: editingBlock.title,
        block_type: editingBlock.block_type,
        days_of_week: editingBlock.days_of_week,
        start_time: editingBlock.start_time,
        end_time: editingBlock.end_time,
        notes: editingBlock.notes || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Block updated');
      setShowEditBlockDialog(false);
      setEditingBlock(null);
      fetchBlocks();
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to update block');
    }
  };
  
  // Delete Block
  const handleDeleteBlock = async (blockId) => {
    if (!confirm('Are you sure you want to delete this block?')) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/calendar-hub/availability-blocks/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Block deleted');
      fetchBlocks();
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to delete block');
    }
  };
  
  // Edit Training Preset
  const handleEditTrainingPreset = async (e) => {
    e.preventDefault();
    if (!editingTraining) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/calendar-hub/training-presets/${editingTraining.preset_id}`, {
        name: editingTraining.name,
        training_type: editingTraining.training_type,
        days_of_week: editingTraining.days_of_week,
        start_time: editingTraining.start_time,
        end_time: editingTraining.end_time,
        location: editingTraining.location || '',
        coach_name: editingTraining.coach_name || '',
        notes: editingTraining.notes || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Training preset updated');
      setShowEditTrainingDialog(false);
      setEditingTraining(null);
      fetchTrainingPresets();
      fetchWeeklyTrainingSchedule();
    } catch (error) {
      toast.error('Failed to update training preset');
    }
  };
  
  // Delete Tournament
  const handleDeleteTournament = async (tournamentId) => {
    if (!confirm('Are you sure you want to delete this tournament and all its games?')) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/calendar-hub/tournaments/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tournament deleted');
      fetchCalendarData();
      fetchTournaments();
    } catch (error) {
      toast.error('Failed to delete tournament');
    }
  };

  const handleGenerateSmartSchedule = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      
      const response = await axios.post(`${API}/calendar-hub/smart-schedule`, {
        ...smartScheduleRequest,
        week_start: weekStart
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setSuggestedSlots(response.data.suggested_schedule || []);
      setSelectedSlots(response.data.suggested_schedule?.map(s => s.date + s.time) || []);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to generate schedule');
    }
  };

  const handleAcceptSchedule = async () => {
    const slotsToAccept = suggestedSlots.filter(s => selectedSlots.includes(s.date + s.time));
    
    if (slotsToAccept.length === 0) {
      toast.error('Select at least one workout slot');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/calendar-hub/smart-schedule/accept`, slotsToAccept, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Committed to ${slotsToAccept.length} workouts!`);
      setShowSmartScheduleDialog(false);
      setSuggestedSlots([]);
      setSelectedSlots([]);
      fetchCalendarData();
      fetchAdherenceStats();
      
      // Show completion prompt (setup complete!)
      if (!showSetupWizard) {
        setNextStepType('after_schedule');
        setShowNextStepPrompt(true);
      }
    } catch (error) {
      toast.error('Failed to accept schedule');
    }
  };

  const handleCompleteCommitment = async (commitmentId) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/calendar-hub/commitments/${commitmentId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchCalendarData();
      fetchAdherenceStats();
    } catch (error) {
      toast.error('Failed to complete commitment');
    }
  };

  const handleMissCommitment = async (commitmentId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/calendar-hub/commitments/${commitmentId}/miss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.info('Commitment marked as missed');
      fetchCalendarData();
      fetchAdherenceStats();
    } catch (error) {
      toast.error('Failed to update commitment');
    }
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!newTournament.name || !newTournament.location || !newTournament.start_date) {
      toast.error('Please fill in tournament name, location, and start date');
      return;
    }
    
    const validGames = newTournament.games.filter(g => g.start_time);
    if (validGames.length === 0) {
      toast.error('Add at least one game with a start time');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/calendar-hub/tournaments`, {
        ...newTournament,
        games: validGames,
        end_date: newTournament.end_date || newTournament.start_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Tournament created with ${response.data.games?.length || 0} games and ${response.data.prep_recovery_windows?.length || 0} prep/recovery windows`);
      setShowTournamentDialog(false);
      setNewTournament({
        name: '', location: '', start_date: '', end_date: '',
        games: [{ opponent: '', start_time: '', end_time: '' }],
        notes: ''
      });
      fetchCalendarData();
      
      // If in wizard mode, advance to next step
      if (showSetupWizard && wizardStep === 3) {
        setWizardStep(4);
        setTimeout(() => setShowSetupWizard(true), 300);
      } else if (!showSetupWizard) {
        // Show smart prompt for next step
        setNextStepType('after_tournament');
        setShowNextStepPrompt(true);
      }
      fetchTournaments();
    } catch (error) {
      toast.error('Failed to create tournament');
    }
  };

  const addGameToTournament = () => {
    setNewTournament(prev => ({
      ...prev,
      games: [...prev.games, { opponent: '', start_time: '', end_time: '' }]
    }));
  };

  const updateGame = (index, field, value) => {
    setNewTournament(prev => ({
      ...prev,
      games: prev.games.map((g, i) => i === index ? { ...g, [field]: value } : g)
    }));
  };

  const removeGame = (index) => {
    if (newTournament.games.length > 1) {
      setNewTournament(prev => ({
        ...prev,
        games: prev.games.filter((_, i) => i !== index)
      }));
    }
  };

  const toggleDaySelection = (dayNum) => {
    setNewBlock(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayNum)
        ? prev.days_of_week.filter(d => d !== dayNum)
        : [...prev.days_of_week, dayNum]
    }));
  };

  const getBlockIcon = (type) => {
    const blockType = BLOCK_TYPES.find(b => b.value === type);
    return blockType?.icon || Clock;
  };

  const getBlockColor = (type) => {
    const blockType = BLOCK_TYPES.find(b => b.value === type);
    return blockType?.color || 'bg-gray-400';
  };

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
  
  // Wizard step handler
  const handleWizardNext = () => {
    if (wizardStep < 4) {
      setWizardStep(wizardStep + 1);
    } else {
      // Wizard complete
      localStorage.setItem('aura_calendar_setup_completed', 'true');
      setWizardCompleted(true);
      setShowSetupWizard(false);
      toast.success("Your calendar is set up! Now let's schedule your workouts.");
      setShowSmartScheduleDialog(true);
    }
  };
  
  const handleWizardSkip = () => {
    localStorage.setItem('aura_calendar_setup_completed', 'true');
    setShowSetupWizard(false);
    toast.info('You can set up your calendar anytime from the buttons above.');
  };
  
  // Get prompt content based on action type
  const getNextStepContent = () => {
    switch (nextStepType) {
      case 'after_block':
        return {
          title: 'Block Added!',
          message: 'Great! The scheduler now knows when you\'re unavailable.',
          options: [
            { label: 'Add Another Block', action: () => { setShowNextStepPrompt(false); setShowBlockDialog(true); }, icon: Clock, primary: false },
            { label: 'Add Training Sessions', action: () => { setShowNextStepPrompt(false); setShowTrainingDialog(true); }, icon: Dumbbell, primary: false },
            { label: 'Generate My Schedule', action: () => { setShowNextStepPrompt(false); setShowSmartScheduleDialog(true); }, icon: Sparkles, primary: true }
          ]
        };
      case 'after_training':
        return {
          title: 'Training Added!',
          message: 'Your recurring training is now on the calendar.',
          options: [
            { label: 'Add Another Training', action: () => { setShowNextStepPrompt(false); setShowTrainingDialog(true); }, icon: Dumbbell, primary: false },
            { label: 'Add a Tournament', action: () => { setShowNextStepPrompt(false); setShowTournamentDialog(true); }, icon: Trophy, primary: false },
            { label: 'Generate My Schedule', action: () => { setShowNextStepPrompt(false); setShowSmartScheduleDialog(true); }, icon: Sparkles, primary: true }
          ]
        };
      case 'after_tournament':
        return {
          title: 'Tournament Created!',
          message: 'Prep and recovery windows have been auto-generated.',
          options: [
            { label: 'Add Another Tournament', action: () => { setShowNextStepPrompt(false); setShowTournamentDialog(true); }, icon: Trophy, primary: false },
            { label: 'Generate Workout Schedule', action: () => { setShowNextStepPrompt(false); setShowSmartScheduleDialog(true); }, icon: Sparkles, primary: true }
          ]
        };
      case 'after_schedule':
        return {
          title: 'Schedule Complete!',
          message: 'Your workouts are locked in. Time to execute!',
          options: [
            { label: 'View My Week', action: () => { setShowNextStepPrompt(false); }, icon: Calendar, primary: true }
          ],
          celebration: true
        };
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      {/* Setup Wizard Modal */}
      <Dialog open={showSetupWizard} onOpenChange={setShowSetupWizard}>
        <DialogContent className="bg-aura-surface border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-white uppercase flex items-center gap-2">
              <Rocket className="w-5 h-5 text-aura-primary" />
              Set Up Your Calendar
            </DialogTitle>
          </DialogHeader>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step < wizardStep ? 'bg-green-500 text-white' :
                  step === wizardStep ? 'bg-aura-primary text-white' :
                  'bg-white/10 text-aura-text-muted'
                }`}>
                  {step < wizardStep ? <CheckCircle className="w-5 h-5" /> : step}
                </div>
                {step < 4 && (
                  <div className={`w-12 h-1 mx-1 rounded ${step < wizardStep ? 'bg-green-500' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Wizard Content */}
          <div className="space-y-4">
            {wizardStep === 1 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Step 1: When Are You Unavailable?</h3>
                <p className="text-sm text-aura-text-muted mb-4">
                  Add times when you CAN'T train — school, church, work, sleep. We'll schedule around these.
                </p>
                <Button 
                  onClick={() => { setShowSetupWizard(false); setShowBlockDialog(true); }}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Availability Block
                </Button>
                <p className="text-xs text-aura-text-muted mt-3">
                  Or <button onClick={handleWizardNext} className="text-aura-primary hover:underline">skip this step</button>
                </p>
              </div>
            )}
            
            {wizardStep === 2 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <Dumbbell className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Step 2: Do You Have Recurring Training?</h3>
                <p className="text-sm text-aura-text-muted mb-4">
                  Team practice, personal training, S&C sessions — add them here and earn Aura points!
                </p>
                <Button 
                  onClick={() => { setShowSetupWizard(false); setShowTrainingDialog(true); }}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Training Preset
                </Button>
                <p className="text-xs text-aura-text-muted mt-3">
                  Or <button onClick={handleWizardNext} className="text-aura-primary hover:underline">skip this step</button>
                </p>
              </div>
            )}
            
            {wizardStep === 3 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Step 3: Any Upcoming Games?</h3>
                <p className="text-sm text-aura-text-muted mb-4">
                  Add tournaments and we'll auto-create prep and recovery windows around each game.
                </p>
                <Button 
                  onClick={() => { setShowSetupWizard(false); setShowTournamentDialog(true); }}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tournament
                </Button>
                <p className="text-xs text-aura-text-muted mt-3">
                  Or <button onClick={handleWizardNext} className="text-aura-primary hover:underline">skip this step</button>
                </p>
              </div>
            )}
            
            {wizardStep === 4 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-aura-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-aura-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Step 4: Generate Your Schedule!</h3>
                <p className="text-sm text-aura-text-muted mb-4">
                  Based on your availability, we'll find the best times for your workouts.
                </p>
                <Button onClick={handleWizardNext} className="bg-aura-primary hover:bg-aura-primary-hover">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate My Schedule
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={handleWizardSkip} className="text-aura-text-muted hover:text-white">
              Skip Setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Smart Next Step Prompt */}
      <Dialog open={showNextStepPrompt} onOpenChange={setShowNextStepPrompt}>
        <DialogContent className="bg-aura-surface border-white/10 max-w-md">
          {getNextStepContent() && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-white uppercase flex items-center gap-2">
                  {getNextStepContent().celebration ? (
                    <PartyPopper className="w-5 h-5 text-green-400" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                  {getNextStepContent().title}
                </DialogTitle>
              </DialogHeader>
              
              <p className="text-aura-text-muted text-sm mb-4">
                {getNextStepContent().message}
              </p>
              
              <div className="space-y-2">
                {getNextStepContent().options.map((opt, idx) => (
                  <Button
                    key={idx}
                    onClick={opt.action}
                    className={`w-full justify-start ${
                      opt.primary 
                        ? 'bg-aura-primary hover:bg-aura-primary-hover' 
                        : 'bg-white/5 hover:bg-white/10 text-white'
                    }`}
                    variant={opt.primary ? 'default' : 'outline'}
                  >
                    <opt.icon className="w-4 h-4 mr-2" />
                    {opt.label}
                    {opt.primary && <ArrowRight className="w-4 h-4 ml-auto" />}
                  </Button>
                ))}
              </div>
              
              <Button 
                variant="ghost" 
                onClick={() => setShowNextStepPrompt(false)} 
                className="w-full mt-2 text-aura-text-muted hover:text-white"
              >
                I'll do this later
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
              Calendar Hub
            </h1>
            <p className="text-aura-text-muted text-sm">Your schedule. Your commitments. Your success.</p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Manage Button */}
            <Dialog open={showManageItemsDialog} onOpenChange={setShowManageItemsDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Manage
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Manage Calendar Items</DialogTitle>
                </DialogHeader>
                
                {/* Tabs */}
                <div className="flex gap-2 border-b border-white/10 pb-2 mb-4">
                  {[
                    { id: 'blocks', label: 'Blocks', count: blocks.length },
                    { id: 'training', label: 'Training', count: trainingPresets.length },
                    { id: 'tournaments', label: 'Tournaments', count: tournaments?.length || 0 }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setManageItemsTab(tab.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        manageItemsTab === tab.id
                          ? 'bg-aura-primary text-white'
                          : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                      }`}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
                
                {/* Blocks Tab */}
                {manageItemsTab === 'blocks' && (
                  <div className="space-y-2">
                    {blocks.length === 0 ? (
                      <p className="text-aura-text-muted text-sm text-center py-4">No availability blocks yet</p>
                    ) : (
                      blocks.map((block) => {
                        const Icon = getBlockIcon(block.block_type);
                        return (
                          <div key={block.block_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getBlockColor(block.block_type)}`}>
                                <Icon className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">{block.title}</p>
                                <p className="text-xs text-aura-text-muted">
                                  {block.days_of_week?.map(d => DAY_NAMES[d]).join(', ')} • {block.start_time}-{block.end_time}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingBlock({ ...block });
                                  setShowEditBlockDialog(true);
                                  setShowManageItemsDialog(false);
                                }}
                                className="text-aura-text-muted hover:text-white"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteBlock(block.block_id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                
                {/* Training Tab */}
                {manageItemsTab === 'training' && (
                  <div className="space-y-2">
                    {trainingPresets.length === 0 ? (
                      <p className="text-aura-text-muted text-sm text-center py-4">No training presets yet</p>
                    ) : (
                      trainingPresets.map((preset) => (
                        <div key={preset.preset_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-500/20">
                              <Dumbbell className="w-4 h-4 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{preset.name}</p>
                              <p className="text-xs text-aura-text-muted">
                                {preset.days_of_week?.map(d => DAY_NAMES[d]).join(', ')} • {preset.start_time}-{preset.end_time}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingTraining({ ...preset });
                                setShowEditTrainingDialog(true);
                                setShowManageItemsDialog(false);
                              }}
                              className="text-aura-text-muted hover:text-white"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteTrainingPreset(preset.preset_id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                {/* Tournaments Tab */}
                {manageItemsTab === 'tournaments' && (
                  <div className="space-y-2">
                    {(!tournaments || tournaments.length === 0) ? (
                      <p className="text-aura-text-muted text-sm text-center py-4">No tournaments yet</p>
                    ) : (
                      tournaments.map((tournament) => (
                        <div key={tournament.tournament_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-500/20">
                              <Trophy className="w-4 h-4 text-green-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{tournament.name}</p>
                              <p className="text-xs text-aura-text-muted">
                                {tournament.start_date} - {tournament.end_date} • {tournament.games?.length || 0} games
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTournament(tournament.tournament_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                    <p className="text-xs text-aura-text-muted mt-2">
                      Note: Editing tournaments is not available. Delete and recreate if changes are needed.
                    </p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            <Dialog open={showBlockDialog} onOpenChange={(open) => {
              setShowBlockDialog(open);
              if (!open) setBlockSuggestion(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <Clock className="w-4 h-4 mr-2" />
                  Add Block
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Add Availability Block</DialogTitle>
                </DialogHeader>
                
                {/* Guidance Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-2">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-400 font-medium">What are Availability Blocks?</p>
                      <p className="text-xs text-blue-300/80 mt-1">
                        Times when you're <strong>NOT available</strong> to train. The smart scheduler will avoid these times.
                      </p>
                      <p className="text-xs text-aura-text-muted mt-1">
                        Examples: School (8am-3pm), Church (Sun 10am-12pm), Sleep (10pm-6am)
                      </p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleCreateBlock} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Block Name</Label>
                    <Input
                      value={newBlock.title}
                      onChange={(e) => {
                        const title = e.target.value;
                        setNewBlock({ ...newBlock, title });
                        setBlockSuggestion(detectMisplacedItem(title, 'block'));
                      }}
                      placeholder="e.g., School, Church, Work"
                      required
                      className="bg-aura-bg border-white/10 text-white"
                      data-testid="block-name-input"
                    />
                    
                    {/* Smart Suggestion Warning */}
                    {blockSuggestion && (
                      <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-2.5 mt-2">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-yellow-300">{blockSuggestion.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Type</Label>
                    <Select value={newBlock.block_type} onValueChange={(v) => setNewBlock({ ...newBlock, block_type: v })}>
                      <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        {BLOCK_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${type.color}`} />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Days</Label>
                    <div className="flex gap-2">
                      {DAY_NAMES.map((day, idx) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDaySelection(idx)}
                          className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${
                            newBlock.days_of_week.includes(idx)
                              ? 'bg-aura-primary text-white'
                              : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Start Time</Label>
                      <Input
                        type="time"
                        value={newBlock.start_time}
                        onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">End Time</Label>
                      <Input
                        type="time"
                        value={newBlock.end_time}
                        onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full bg-aura-primary hover:bg-aura-primary-hover">
                    Create Block
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showSmartScheduleDialog} onOpenChange={setShowSmartScheduleDialog}>
              <DialogTrigger asChild>
                <Button className="bg-aura-primary hover:bg-aura-primary-hover">
                  <Brain className="w-4 h-4 mr-2" />
                  Smart Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">AI Workout Scheduler</DialogTitle>
                </DialogHeader>
                
                {suggestedSlots.length === 0 ? (
                  <div className="space-y-4">
                    <p className="text-aura-text-muted text-sm">
                      We'll analyze your blocks, games, and goals to find optimal workout times.
                    </p>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Workouts Per Week</Label>
                      <Select 
                        value={String(smartScheduleRequest.workouts_per_week)} 
                        onValueChange={(v) => setSmartScheduleRequest({ ...smartScheduleRequest, workouts_per_week: parseInt(v) })}
                      >
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {[3, 4, 5, 6].map(n => (
                            <SelectItem key={n} value={String(n)} className="text-white">{n} workouts</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Preferred Times</Label>
                      <div className="flex gap-2">
                        {['morning', 'afternoon', 'evening'].map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => {
                              const times = smartScheduleRequest.preferred_times.includes(time)
                                ? smartScheduleRequest.preferred_times.filter(t => t !== time)
                                : [...smartScheduleRequest.preferred_times, time];
                              setSmartScheduleRequest({ ...smartScheduleRequest, preferred_times: times });
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                              smartScheduleRequest.preferred_times.includes(time)
                                ? 'bg-aura-primary text-white'
                                : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={smartScheduleRequest.avoid_game_days}
                        onCheckedChange={(checked) => setSmartScheduleRequest({ ...smartScheduleRequest, avoid_game_days: checked })}
                      />
                      <span className="text-sm text-white">Avoid game days</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={smartScheduleRequest.recovery_day_after_games}
                        onCheckedChange={(checked) => setSmartScheduleRequest({ ...smartScheduleRequest, recovery_day_after_games: checked })}
                      />
                      <span className="text-sm text-white">Recovery day after games</span>
                    </div>
                    
                    <Button onClick={handleGenerateSmartSchedule} className="w-full bg-aura-primary hover:bg-aura-primary-hover">
                      <Zap className="w-4 h-4 mr-2" />
                      Generate Schedule
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-aura-text-muted text-sm">
                      Select the workout slots you want to commit to:
                    </p>
                    
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {suggestedSlots.map((slot, idx) => (
                        <div 
                          key={idx}
                          onClick={() => {
                            const key = slot.date + slot.time;
                            setSelectedSlots(prev => 
                              prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                            );
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedSlots.includes(slot.date + slot.time)
                              ? 'bg-aura-primary/20 border-aura-primary'
                              : 'bg-white/5 border-white/10 hover:border-white/20'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">{slot.day_name}, {slot.date}</p>
                              <p className="text-sm text-aura-text-muted">{slot.time} - {slot.end_time}</p>
                            </div>
                            <Badge className={`${
                              slot.suggested_intensity === 'high' ? 'bg-red-500/20 text-red-400' :
                              slot.suggested_intensity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            } border-0`}>
                              {slot.suggested_intensity}
                            </Badge>
                          </div>
                          <p className="text-xs text-aura-text-muted mt-1">{slot.reason}</p>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => { setSuggestedSlots([]); setSelectedSlots([]); }}
                        className="flex-1 border-white/20 text-white"
                      >
                        Back
                      </Button>
                      <Button onClick={handleAcceptSchedule} className="flex-1 bg-aura-primary hover:bg-aura-primary-hover">
                        Commit to {selectedSlots.length} Workouts
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Tournament Dialog */}
            {/* Tournament Options - Two buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                onClick={() => setShowTournamentWizard(true)}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Tournament Wizard
              </Button>
              
              <Dialog open={showTournamentDialog} onOpenChange={(open) => {
              setShowTournamentDialog(open);
              if (!open) setTournamentSuggestion(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10">
                  <Trophy className="w-4 h-4 mr-2" />
                  Quick Add
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Add Tournament</DialogTitle>
                </DialogHeader>
                
                {/* Guidance Box */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-2">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-green-400 font-medium">What are Tournaments?</p>
                      <p className="text-xs text-green-300/80 mt-1">
                        <strong>Games and competitions</strong> — Aura auto-creates prep windows (warm-up, hydration) and recovery windows between games.
                      </p>
                      <p className="text-xs text-aura-text-muted mt-1">
                        Examples: Spring Showcase, Weekend Tournament, Championship Game
                      </p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleCreateTournament} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Tournament Name</Label>
                      <Input
                        value={newTournament.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setNewTournament({ ...newTournament, name });
                          setTournamentSuggestion(detectMisplacedItem(name, 'tournament'));
                        }}
                        placeholder="e.g., Spring Showcase, State Championship"
                        required
                        className="bg-aura-bg border-white/10 text-white"
                        data-testid="tournament-name-input"
                      />
                      
                      {/* Smart Suggestion Warning */}
                      {tournamentSuggestion && (
                        <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-2.5 mt-2">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-yellow-300">{tournamentSuggestion.message}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Location</Label>
                      <Input
                        value={newTournament.location}
                        onChange={(e) => setNewTournament({ ...newTournament, location: e.target.value })}
                        placeholder="e.g., Sports Complex"
                        required
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Start Date</Label>
                      <Input
                        type="date"
                        value={newTournament.start_date}
                        onChange={(e) => setNewTournament({ ...newTournament, start_date: e.target.value })}
                        required
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">End Date</Label>
                      <Input
                        type="date"
                        value={newTournament.end_date}
                        onChange={(e) => setNewTournament({ ...newTournament, end_date: e.target.value })}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase text-aura-text-muted">Games</Label>
                      <Button type="button" size="sm" variant="ghost" onClick={addGameToTournament} className="text-aura-primary">
                        <Plus className="w-4 h-4 mr-1" /> Add Game
                      </Button>
                    </div>
                    
                    {newTournament.games.map((game, idx) => (
                      <div key={idx} className="p-3 bg-white/5 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">Game {idx + 1}</span>
                          {newTournament.games.length > 1 && (
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => removeGame(idx)}
                              className="text-red-400 hover:text-red-300 h-6 px-2"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <Input
                          placeholder="Opponent (e.g., Eagles)"
                          value={game.opponent}
                          onChange={(e) => updateGame(idx, 'opponent', e.target.value)}
                          className="bg-aura-bg border-white/10 text-white"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase text-aura-text-muted">Game Time</Label>
                            <Input
                              type="datetime-local"
                              value={game.start_time}
                              onChange={(e) => updateGame(idx, 'start_time', e.target.value)}
                              required
                              className="bg-aura-bg border-white/10 text-white text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-aura-text-muted">End Time</Label>
                            <Input
                              type="datetime-local"
                              value={game.end_time}
                              onChange={(e) => updateGame(idx, 'end_time', e.target.value)}
                              className="bg-aura-bg border-white/10 text-white text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-sm text-green-400">
                      <Trophy className="w-4 h-4 inline mr-1" />
                      Aura will automatically create prep and recovery windows between games!
                    </p>
                  </div>
                  
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    Create Tournament
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            
            {/* Training Preset Dialog */}
            <Dialog open={showTrainingDialog} onOpenChange={(open) => {
              setShowTrainingDialog(open);
              if (!open) setTrainingSuggestion(null);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                  <Dumbbell className="w-4 h-4 mr-2" />
                  Add Training
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Add Training Preset</DialogTitle>
                </DialogHeader>
                
                {/* Guidance Box */}
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-2">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-cyan-400 font-medium">What are Training Presets?</p>
                      <p className="text-xs text-cyan-300/80 mt-1">
                        <strong>Recurring practices & training sessions</strong> that count toward your progress and earn Aura points.
                      </p>
                      <p className="text-xs text-aura-text-muted mt-1">
                        Examples: Team Practice (MWF 4-6pm), Personal Trainer (Tue 5pm), Strength & Conditioning
                      </p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleCreateTrainingPreset} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Training Name</Label>
                    <Input
                      value={newTrainingPreset.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setNewTrainingPreset({ ...newTrainingPreset, name });
                        setTrainingSuggestion(detectMisplacedItem(name, 'training'));
                      }}
                      placeholder="e.g., Team Practice, Weight Room, Speed Training"
                      required
                      className="bg-aura-bg border-white/10 text-white"
                      data-testid="training-name-input"
                    />
                    
                    {/* Smart Suggestion Warning */}
                    {trainingSuggestion && (
                      <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-2.5 mt-2">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-yellow-300">{trainingSuggestion.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Training Type</Label>
                    <Select 
                      value={newTrainingPreset.training_type} 
                      onValueChange={(v) => setNewTrainingPreset({ ...newTrainingPreset, training_type: v })}
                    >
                      <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        {TRAINING_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            <div className="flex items-center justify-between w-full gap-4">
                              <span>{type.label}</span>
                              <Badge className={`${type.color} text-white text-[10px] border-0`}>
                                +{type.points} pts
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-aura-text-muted">Days</Label>
                    <div className="flex gap-2">
                      {DAY_NAMES.map((day, idx) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleTrainingDay(idx)}
                          className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${
                            newTrainingPreset.days_of_week.includes(idx)
                              ? 'bg-cyan-500 text-white'
                              : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Start Time</Label>
                      <Input
                        type="time"
                        value={newTrainingPreset.start_time}
                        onChange={(e) => setNewTrainingPreset({ ...newTrainingPreset, start_time: e.target.value })}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">End Time</Label>
                      <Input
                        type="time"
                        value={newTrainingPreset.end_time}
                        onChange={(e) => setNewTrainingPreset({ ...newTrainingPreset, end_time: e.target.value })}
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Location (Optional)</Label>
                      <Input
                        value={newTrainingPreset.location}
                        onChange={(e) => setNewTrainingPreset({ ...newTrainingPreset, location: e.target.value })}
                        placeholder="e.g., Main Gym"
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Coach (Optional)</Label>
                      <Input
                        value={newTrainingPreset.coach_name}
                        onChange={(e) => setNewTrainingPreset({ ...newTrainingPreset, coach_name: e.target.value })}
                        placeholder="e.g., Coach Smith"
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                    <p className="text-sm text-cyan-400">
                      <Target className="w-4 h-4 inline mr-1" />
                      Training sessions count towards your progress and award Aura points!
                    </p>
                  </div>
                  
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    Create Training Preset
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Edit Block Dialog */}
            <Dialog open={showEditBlockDialog} onOpenChange={(open) => {
              setShowEditBlockDialog(open);
              if (!open) setEditingBlock(null);
            }}>
              <DialogContent className="bg-aura-surface border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Edit Availability Block</DialogTitle>
                </DialogHeader>
                
                {editingBlock && (
                  <form onSubmit={handleEditBlock} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Block Name</Label>
                      <Input
                        value={editingBlock.title}
                        onChange={(e) => setEditingBlock({ ...editingBlock, title: e.target.value })}
                        required
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Type</Label>
                      <Select value={editingBlock.block_type} onValueChange={(v) => setEditingBlock({ ...editingBlock, block_type: v })}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {BLOCK_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="text-white">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${type.color}`} />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Days</Label>
                      <div className="flex gap-2">
                        {DAY_NAMES.map((day, idx) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => setEditingBlock({
                              ...editingBlock,
                              days_of_week: editingBlock.days_of_week.includes(idx)
                                ? editingBlock.days_of_week.filter(d => d !== idx)
                                : [...editingBlock.days_of_week, idx]
                            })}
                            className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${
                              editingBlock.days_of_week?.includes(idx)
                                ? 'bg-aura-primary text-white'
                                : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">Start Time</Label>
                        <Input
                          type="time"
                          value={editingBlock.start_time}
                          onChange={(e) => setEditingBlock({ ...editingBlock, start_time: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">End Time</Label>
                        <Input
                          type="time"
                          value={editingBlock.end_time}
                          onChange={(e) => setEditingBlock({ ...editingBlock, end_time: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1 bg-aura-primary hover:bg-aura-primary-hover">
                        Save Changes
                      </Button>
                      <Button 
                        type="button" 
                        variant="destructive" 
                        onClick={() => {
                          handleDeleteBlock(editingBlock.block_id);
                          setShowEditBlockDialog(false);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Edit Training Dialog */}
            <Dialog open={showEditTrainingDialog} onOpenChange={(open) => {
              setShowEditTrainingDialog(open);
              if (!open) setEditingTraining(null);
            }}>
              <DialogContent className="bg-aura-surface border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-white uppercase">Edit Training Preset</DialogTitle>
                </DialogHeader>
                
                {editingTraining && (
                  <form onSubmit={handleEditTrainingPreset} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Training Name</Label>
                      <Input
                        value={editingTraining.name}
                        onChange={(e) => setEditingTraining({ ...editingTraining, name: e.target.value })}
                        required
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Training Type</Label>
                      <Select value={editingTraining.training_type} onValueChange={(v) => setEditingTraining({ ...editingTraining, training_type: v })}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {TRAINING_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="text-white">
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-aura-text-muted">Days</Label>
                      <div className="flex gap-2">
                        {DAY_NAMES.map((day, idx) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => setEditingTraining({
                              ...editingTraining,
                              days_of_week: editingTraining.days_of_week.includes(idx)
                                ? editingTraining.days_of_week.filter(d => d !== idx)
                                : [...editingTraining.days_of_week, idx]
                            })}
                            className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${
                              editingTraining.days_of_week?.includes(idx)
                                ? 'bg-cyan-500 text-white'
                                : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">Start Time</Label>
                        <Input
                          type="time"
                          value={editingTraining.start_time}
                          onChange={(e) => setEditingTraining({ ...editingTraining, start_time: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">End Time</Label>
                        <Input
                          type="time"
                          value={editingTraining.end_time}
                          onChange={(e) => setEditingTraining({ ...editingTraining, end_time: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">Location (Optional)</Label>
                        <Input
                          value={editingTraining.location || ''}
                          onChange={(e) => setEditingTraining({ ...editingTraining, location: e.target.value })}
                          placeholder="e.g., Main Gym"
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-aura-text-muted">Coach (Optional)</Label>
                        <Input
                          value={editingTraining.coach_name || ''}
                          onChange={(e) => setEditingTraining({ ...editingTraining, coach_name: e.target.value })}
                          placeholder="e.g., Coach Smith"
                          className="bg-aura-bg border-white/10 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600">
                        Save Changes
                      </Button>
                      <Button 
                        type="button" 
                        variant="destructive" 
                        onClick={() => {
                          handleDeleteTrainingPreset(editingTraining.preset_id);
                          setShowEditTrainingDialog(false);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Adherence Stats */}
        {adherenceStats && (
          <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-aura-primary/20 flex items-center justify-center">
                    <Target className="w-7 h-7 text-aura-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {adherenceStats.adherence_rate}%
                    </p>
                    <p className="text-sm text-aura-text-muted">Commitment Adherence</p>
                  </div>
                </div>
                
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-400">{adherenceStats.completed}</p>
                    <p className="text-xs text-aura-text-muted">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-red-400">{adherenceStats.missed}</p>
                    <p className="text-xs text-aura-text-muted">Missed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-orange-400">{adherenceStats.current_streak}</p>
                    <p className="text-xs text-aura-text-muted">Streak</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} className="text-white">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h2 className="font-heading font-bold text-lg text-white">
            {format(currentWeek, 'MMM d')} - {format(endOfWeek(currentWeek, { weekStartsOn: 0 }), 'MMM d, yyyy')}
          </h2>
          <Button variant="ghost" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} className="text-white">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Active Blocks Summary */}
        {blocks.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {blocks.map((block) => {
              const Icon = getBlockIcon(block.block_type);
              return (
                <Badge key={block.block_id} className={`${getBlockColor(block.block_type)} text-white border-0 px-3 py-1`}>
                  <Icon className="w-3 h-3 mr-1" />
                  {block.title}: {block.start_time}-{block.end_time}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Week View */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayData = calendarData[dateStr] || { events: [], commitments: [], blocks: [], tournaments: [] };
            const isCurrentDay = isToday(day);
            
            return (
              <div key={dateStr} className={`min-h-[200px] rounded-xl border p-2 ${
                isCurrentDay ? 'bg-aura-primary/10 border-aura-primary/50' : 'bg-aura-surface border-white/10'
              }`}>
                {/* Day Header */}
                <div className="text-center mb-2">
                  <p className="text-xs text-aura-text-muted uppercase">{format(day, 'EEE')}</p>
                  <p className={`text-lg font-bold ${isCurrentDay ? 'text-aura-primary' : 'text-white'}`}>
                    {format(day, 'd')}
                  </p>
                </div>
                
                {/* Day Content */}
                <div className="space-y-1">
                  {/* Blocks */}
                  {dayData.blocks.map((block, idx) => (
                    <div key={idx} className={`${getBlockColor(block.block_type)} rounded px-1.5 py-0.5`}>
                      <p className="text-[10px] text-white truncate">{block.title}</p>
                      <p className="text-[9px] text-white/70">{block.start_time}-{block.end_time}</p>
                    </div>
                  ))}
                  
                  {/* Events */}
                  {dayData.events.map((event, idx) => (
                    <div key={idx} className={`rounded px-1.5 py-0.5 ${
                      event.event_type === 'game' ? 'bg-green-500/30 border-l-2 border-green-500' :
                      event.event_type === 'practice' ? 'bg-aura-primary/30 border-l-2 border-aura-primary' :
                      event.event_type === 'prep' ? 'bg-yellow-500/30 border-l-2 border-yellow-500' :
                      event.event_type === 'recovery' ? 'bg-purple-500/30 border-l-2 border-purple-500' :
                      'bg-white/10 border-l-2 border-white/30'
                    }`}>
                      <p className="text-[10px] text-white truncate">{event.title}</p>
                      <p className="text-[9px] text-white/70">
                        {event.start_time?.substring(11, 16)}
                      </p>
                    </div>
                  ))}
                  
                  {/* Commitments */}
                  {dayData.commitments.map((commit, idx) => (
                    <div key={idx} className={`rounded px-1.5 py-0.5 ${
                      commit.status === 'completed' ? 'bg-green-500/30' :
                      commit.status === 'missed' ? 'bg-red-500/30' :
                      'bg-aura-secondary/30 border-l-2 border-aura-secondary'
                    }`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-white truncate">
                          <Dumbbell className="w-2.5 h-2.5 inline mr-1" />
                          Workout
                        </p>
                        {commit.status === 'scheduled' && isToday(day) && (
                          <div className="flex gap-0.5">
                            <button 
                              onClick={() => handleCompleteCommitment(commit.commitment_id)}
                              className="p-0.5 hover:bg-green-500/30 rounded"
                            >
                              <CheckCircle className="w-3 h-3 text-green-400" />
                            </button>
                            <button 
                              onClick={() => handleMissCommitment(commit.commitment_id)}
                              className="p-0.5 hover:bg-red-500/30 rounded"
                            >
                              <XCircle className="w-3 h-3 text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-[9px] text-white/70">{commit.scheduled_time}</p>
                    </div>
                  ))}
                  
                  {/* Training Sessions */}
                  {weeklyTrainingSchedule?.schedule?.[dateStr]?.sessions?.map((session, idx) => (
                    <div key={`training-${idx}`} className={`rounded px-1.5 py-0.5 border-l-2 ${
                      session.is_completed 
                        ? 'bg-green-500/30 border-green-500' 
                        : 'bg-cyan-500/20 border-cyan-500'
                    }`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-white truncate">
                          {session.name}
                        </p>
                        {!session.is_completed && (
                          <button 
                            onClick={() => handleCompleteTraining(session.preset_id, dateStr)}
                            className="p-0.5 hover:bg-green-500/30 rounded"
                            title="Mark complete"
                          >
                            <CheckCircle className="w-3 h-3 text-cyan-400 hover:text-green-400" />
                          </button>
                        )}
                        {session.is_completed && (
                          <CheckCircle className="w-3 h-3 text-green-400" />
                        )}
                      </div>
                      <p className="text-[9px] text-white/70">{session.start_time}-{session.end_time}</p>
                      {session.location && (
                        <p className="text-[8px] text-aura-text-muted truncate">{session.location}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Tips */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 mb-4">
              <Flame className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Build Your Routine</p>
                <p className="text-sm text-aura-text-muted">
                  {blocks.length === 0 
                    ? "Start by adding your recurring commitments (school, church, etc.) so we can find optimal workout times."
                    : adherenceStats?.adherence_rate < 70
                    ? "Your adherence is below 70%. Try committing to fewer workouts to build consistency."
                    : "Great consistency! Consider adding an extra workout day to accelerate your progress."
                  }
                </p>
              </div>
            </div>
            
            {/* Event Type Guide */}
            <div className="border-t border-white/10 pt-4 mt-2">
              <p className="text-xs uppercase text-aura-text-muted font-semibold mb-3 flex items-center gap-2">
                <Info className="w-3.5 h-3.5" />
                What to Add Where
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-blue-400">Blocks</span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    Unavailable time — School, Church, Work, Sleep
                  </p>
                </div>
                <div className="bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Dumbbell className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-medium text-cyan-400">Training</span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    Recurring sessions — Practice, S&C, Personal Trainer
                  </p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-green-400">Tournaments</span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    Games & competitions — Auto prep/recovery
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tournament Wizard 2.0 */}
      <TournamentWizard 
        open={showTournamentWizard} 
        onOpenChange={setShowTournamentWizard}
        onTournamentCreated={() => {
          fetchTournaments();
          fetchEvents();
        }}
      />
    </DashboardLayout>
  );
};

export default CalendarHubPage;
